import { useMemo, useState, type CSSProperties } from "react";
import { motion } from "framer-motion";
import { ArrowsLeftRight } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";

export type DiffLineType = "add" | "remove" | "unchanged";

export interface DiffLine {
  type: DiffLineType;
  content: string;
  oldLineNumber?: number;
  newLineNumber?: number;
}

/**
 * Computes line-by-line diff using longest common subsequence (LCS).
 * Returns an array of DiffLine objects for inline display.
 */
function computeDiff(oldContent: string, newContent: string): DiffLine[] {
  const oldLines = oldContent.split("\n");
  const newLines = newContent.split("\n");

  const m = oldLines.length;
  const n = newLines.length;

  // Build LCS length table
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to build diff
  const result: DiffLine[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.unshift({
        type: "unchanged",
        content: oldLines[i - 1],
        oldLineNumber: i,
        newLineNumber: j,
      });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({
        type: "add",
        content: newLines[j - 1],
        newLineNumber: j,
      });
      j--;
    } else {
      result.unshift({
        type: "remove",
        content: oldLines[i - 1],
        oldLineNumber: i,
      });
      i--;
    }
  }

  return result;
}

interface DiffViewerProps {
  oldContent: string;
  newContent: string;
  /** Flat border-only chrome for embedding in drawers/panels (no nested “card” surface). */
  embedded?: boolean;
  className?: string;
}

const embedShell = "border border-[var(--border-hairline)] rounded-[var(--radius-sm)] bg-[var(--bg-secondary)]";
const cardShell = "lever-surface overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border-hairline)]";

export function DiffViewer({ oldContent, newContent, embedded = false, className }: DiffViewerProps) {
  const [mode, setMode] = useState<"inline" | "side-by-side">("inline");

  const diffLines = useMemo(
    () => computeDiff(oldContent, newContent),
    [oldContent, newContent]
  );

  const lineHeight = "1.55";
  const lineNumWidth = "3ch";

  const gutterCell = (num: number | undefined, type: DiffLineType) => (
    <span
      className={cn(
        "inline-block w-[var(--line-num-width)] shrink-0 pr-2 text-right text-[var(--text-muted)] select-none",
        type === "add" && "bg-[var(--color-signal-green)]/10",
        type === "remove" && "bg-[var(--color-soft-red)]/10"
      )}
      style={{ fontFamily: "var(--font-mono)", fontSize: "12px" }}
    >
      {num ?? ""}
    </span>
  );

  const lineContent = (content: string, type: DiffLineType) => (
    <span
      className={cn(
        "flex-1 whitespace-pre-wrap break-all",
        type === "add" && "bg-[var(--color-signal-green)]/10",
        type === "remove" && "bg-[var(--color-soft-red)]/10"
      )}
      style={{ fontFamily: "var(--font-mono)", fontSize: "12px" }}
    >
      {content || " "}
    </span>
  );

  if (oldContent === newContent) {
    return (
      <div
        className={cn(
          embedded
            ? "border-t border-[var(--border-hairline)] px-2 py-3 text-center"
            : "lever-surface rounded-[var(--radius-lg)] p-4",
          className
        )}
      >
        <p className={cn("text-[var(--text-muted)]", embedded ? "text-xs" : "text-sm")}>
          No changes vs current
        </p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", embedded && "min-w-0", className)}>
      {/* Mode toggle */}
      <div className={cn("flex items-center justify-end", embedded && "px-0.5")}>
        <button
          type="button"
          onClick={() => setMode((m) => (m === "inline" ? "side-by-side" : "inline"))}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2 py-1 text-xs",
            "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
            "hover:bg-[var(--bg-surface-hover)]",
            "transition-colors duration-[var(--duration-fast)]"
          )}
        >
          <ArrowsLeftRight size={14} />
          {mode === "inline" ? "Side by side" : "Inline"}
        </button>
      </div>

      <div className={cn(embedded ? embedShell : cardShell, "overflow-hidden")}>
        <div
          className={cn(
            "overflow-auto max-h-[320px]",
            embedded && "px-2 pb-2 pt-1.5"
          )}
          style={
            {
              "--line-num-width": lineNumWidth,
            } as CSSProperties
          }
        >
          {mode === "inline" ? (
            <div className="min-w-0 font-mono">
              {diffLines.map((line, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.1,
                    delay: Math.min(idx * 0.01, 0.3),
                  }}
                  className={cn(
                    "flex w-full border-b border-[var(--border-hairline)] last:border-b-0",
                    "leading-[var(--line-height)]",
                    line.type === "add" && "bg-[var(--color-signal-green)]/10",
                    line.type === "remove" && "bg-[var(--color-soft-red)]/10"
                  )}
                  style={{
                    lineHeight,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {gutterCell(
                    line.type === "remove" ? line.oldLineNumber : line.newLineNumber,
                    line.type
                  )}
                  <span
                    className={cn(
                      "mr-2 shrink-0 text-[10px] font-medium",
                      line.type === "add" && "text-[var(--color-signal-green)]",
                      line.type === "remove" && "text-[var(--color-soft-red)]"
                    )}
                  >
                    {line.type === "add" ? "+" : line.type === "remove" ? "-" : " "}
                  </span>
                  {lineContent(line.content, line.type)}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="flex min-w-0">
              {/* Old content column */}
              <div className="flex-1 min-w-0 border-r border-[var(--border-hairline)]">
                <div className="sticky top-0 z-10 bg-[var(--color-soft-red)]/5 px-2 py-1 text-[10px] font-medium text-[var(--color-soft-red)]">
                  Previous
                </div>
                {diffLines.map((line, idx) => (
                  <div
                    key={`side-old-${idx}`}
                    className={cn(
                      "flex border-b border-[var(--border-hairline)] last:border-b-0",
                      line.type === "remove" && "bg-[var(--color-soft-red)]/10"
                    )}
                    style={{ lineHeight, fontFamily: "var(--font-mono)" }}
                  >
                    {gutterCell(
                      line.type !== "add" ? line.oldLineNumber : undefined,
                      line.type
                    )}
                    <span
                      className={cn(
                        "flex-1 whitespace-pre-wrap break-all px-2",
                        line.type === "remove" && "text-[var(--color-soft-red)]"
                      )}
                      style={{ fontSize: "12px" }}
                    >
                      {line.type === "add" ? " " : line.content || " "}
                    </span>
                  </div>
                ))}
              </div>

              {/* New content column */}
              <div className="flex-1 min-w-0">
                <div className="sticky top-0 z-10 bg-[var(--color-signal-green)]/5 px-2 py-1 text-[10px] font-medium text-[var(--color-signal-green)]">
                  Current
                </div>
                {diffLines.map((line, idx) => (
                  <div
                    key={`side-new-${idx}`}
                    className={cn(
                      "flex border-b border-[var(--border-hairline)] last:border-b-0",
                      line.type === "add" && "bg-[var(--color-signal-green)]/10"
                    )}
                    style={{ lineHeight, fontFamily: "var(--font-mono)" }}
                  >
                    {gutterCell(
                      line.type !== "remove" ? line.newLineNumber : undefined,
                      line.type
                    )}
                    <span
                      className={cn(
                        "flex-1 whitespace-pre-wrap break-all px-2",
                        line.type === "add" && "text-[var(--color-signal-green)]"
                      )}
                      style={{ fontSize: "12px" }}
                    >
                      {line.type === "remove" ? " " : line.content || " "}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
